import React, { useRef, useEffect } from 'react';
import styles from './OtpInput.module.css';

interface OtpInputProps {
  otp: string;
  setOtp: (otp: string) => void;
  numInputs?: number;
  hasError?: boolean;
}

/**
 * A reusable OTP input component for web.
 */
const OtpInput: React.FC<OtpInputProps> = ({ otp, setOtp, numInputs = 6, hasError = false }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus the first empty input on mount or when the component appears
    const firstEmptyInput = inputRefs.current.findIndex((input) => input && !input.value);
    if (firstEmptyInput !== -1) {
      inputRefs.current[firstEmptyInput]?.focus();
    } else {
      inputRefs.current[numInputs - 1]?.focus();
    }
  }, [numInputs]);

  const handleChange = (element: HTMLInputElement, index: number) => {
    const value = element.value.replace(/[^0-9]/g, ''); // Only allow digits
    if (value) {
      // Move to the next field if a digit is entered
      if (index < numInputs - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
    const newOtp = otp.split('');
    newOtp[index] = value;
    setOtp(newOtp.join('').slice(0, numInputs));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move focus to the previous field on backspace if the current field is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const otpBoxes = Array.from({ length: numInputs }, (_, i) => i);

  return (
    <div className={styles.otpContainer}>
      {otpBoxes.map((index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          maxLength={1}
          className={`${styles.otpInput} ${hasError ? styles.otpInputError : ''}`}
          value={otp[index] || ''}
          onChange={(e) => handleChange(e.target, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onFocus={(e) => e.target.select()}
          data-testid={`otp-input-${index}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
